import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useInspections, useInspectionTemplates, useInspectionResponses } from '@/hooks/useInspections';
import { useSites } from '@/hooks/useSites';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { format, subDays, startOfDay, eachDayOfInterval, parseISO } from 'date-fns';
import { 
  TrendingUp, 
  TrendingDown, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Building2,
  ClipboardCheck,
  Calendar
} from 'lucide-react';

const COLORS = {
  pass: 'hsl(var(--chart-2))',
  fail: 'hsl(var(--destructive))',
  na: 'hsl(var(--muted-foreground))'
};

export const InspectionAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState('30');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState('all');

  const { data: inspections = [], isLoading: inspectionsLoading } = useInspections({ status: 'submitted' });
  const { data: templates = [] } = useInspectionTemplates();
  const { sites = [] } = useSites();

  // Filter inspections based on selections
  const filteredInspections = useMemo(() => {
    const daysAgo = parseInt(dateRange);
    const cutoffDate = subDays(new Date(), daysAgo);
    
    return inspections.filter(inspection => {
      const submittedAt = inspection.submitted_at ? parseISO(inspection.submitted_at) : null;
      if (!submittedAt || submittedAt < cutoffDate) return false;
      
      if (selectedSite !== 'all' && inspection.site_id !== selectedSite) return false;
      if (selectedTemplate !== 'all' && inspection.template_id !== selectedTemplate) return false;
      
      return true;
    });
  }, [inspections, dateRange, selectedSite, selectedTemplate]);

  // Calculate overall statistics
  const stats = useMemo(() => {
    let totalPass = 0;
    let totalFail = 0;
    let totalNA = 0;

    filteredInspections.forEach(inspection => {
      const responses = (inspection as any).inspection_responses || [];
      responses.forEach((response: any) => {
        const value = response.response_value?.toUpperCase();
        if (value === 'PASS' || value === 'YES') totalPass++;
        else if (value === 'FAIL' || value === 'NO') totalFail++;
        else if (value === 'NA' || value === 'N/A') totalNA++;
      });
    });

    const total = totalPass + totalFail + totalNA;
    const passRate = total > 0 ? ((totalPass / total) * 100).toFixed(1) : '0';
    const failRate = total > 0 ? ((totalFail / total) * 100).toFixed(1) : '0';

    return {
      totalInspections: filteredInspections.length,
      totalPass,
      totalFail,
      totalNA,
      total,
      passRate,
      failRate
    };
  }, [filteredInspections]);

  // Pie chart data
  const pieData = useMemo(() => [
    { name: 'Pass', value: stats.totalPass, color: COLORS.pass },
    { name: 'Fail', value: stats.totalFail, color: COLORS.fail },
    { name: 'N/A', value: stats.totalNA, color: COLORS.na }
  ].filter(d => d.value > 0), [stats]);

  // Trend data over time
  const trendData = useMemo(() => {
    const daysAgo = parseInt(dateRange);
    const interval = eachDayOfInterval({
      start: subDays(new Date(), daysAgo),
      end: new Date()
    });

    return interval.map(date => {
      const dayStart = startOfDay(date);
      const dayInspections = filteredInspections.filter(inspection => {
        const submittedAt = inspection.submitted_at ? parseISO(inspection.submitted_at) : null;
        return submittedAt && startOfDay(submittedAt).getTime() === dayStart.getTime();
      });

      let passCount = 0;
      let failCount = 0;

      dayInspections.forEach(inspection => {
        const responses = (inspection as any).inspection_responses || [];
        responses.forEach((response: any) => {
          const value = response.response_value?.toUpperCase();
          if (value === 'PASS' || value === 'YES') passCount++;
          else if (value === 'FAIL' || value === 'NO') failCount++;
        });
      });

      const total = passCount + failCount;
      const passRate = total > 0 ? (passCount / total) * 100 : null;

      return {
        date: format(date, 'MMM d'),
        inspections: dayInspections.length,
        passRate,
        passCount,
        failCount
      };
    });
  }, [filteredInspections, dateRange]);

  // Inspections by site
  const siteData = useMemo(() => {
    const siteStats: Record<string, { name: string; count: number; passRate: number }> = {};

    filteredInspections.forEach(inspection => {
      const site = sites.find(s => s.id === inspection.site_id);
      const siteName = site?.name || 'Unassigned';
      
      if (!siteStats[siteName]) {
        siteStats[siteName] = { name: siteName, count: 0, passRate: 0 };
      }
      siteStats[siteName].count++;

      const responses = (inspection as any).inspection_responses || [];
      let pass = 0, total = 0;
      responses.forEach((response: any) => {
        const value = response.response_value?.toUpperCase();
        if (value === 'PASS' || value === 'YES') { pass++; total++; }
        else if (value === 'FAIL' || value === 'NO') { total++; }
      });

      if (total > 0) {
        // Running average
        const currentTotal = siteStats[siteName].count;
        const prevRate = siteStats[siteName].passRate;
        const newRate = (pass / total) * 100;
        siteStats[siteName].passRate = ((prevRate * (currentTotal - 1)) + newRate) / currentTotal;
      }
    });

    return Object.values(siteStats).sort((a, b) => b.count - a.count);
  }, [filteredInspections, sites]);

  // Template performance
  const templateData = useMemo(() => {
    const templateStats: Record<string, { name: string; count: number; passRate: number; failItems: number }> = {};

    filteredInspections.forEach(inspection => {
      const template = templates.find(t => t.id === inspection.template_id);
      const templateName = template?.name || 'Unknown';
      
      if (!templateStats[templateName]) {
        templateStats[templateName] = { name: templateName, count: 0, passRate: 0, failItems: 0 };
      }
      templateStats[templateName].count++;

      const responses = (inspection as any).inspection_responses || [];
      let pass = 0, fail = 0, total = 0;
      responses.forEach((response: any) => {
        const value = response.response_value?.toUpperCase();
        if (value === 'PASS' || value === 'YES') { pass++; total++; }
        else if (value === 'FAIL' || value === 'NO') { fail++; total++; }
      });

      templateStats[templateName].failItems += fail;

      if (total > 0) {
        const currentTotal = templateStats[templateName].count;
        const prevRate = templateStats[templateName].passRate;
        const newRate = (pass / total) * 100;
        templateStats[templateName].passRate = ((prevRate * (currentTotal - 1)) + newRate) / currentTotal;
      }
    });

    return Object.values(templateStats).sort((a, b) => b.count - a.count);
  }, [filteredInspections, templates]);

  if (inspectionsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Analytics Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Date Range</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Facility</label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Facilities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Facilities</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Template</label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Templates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Templates</SelectItem>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Inspections</p>
                <p className="text-3xl font-bold">{stats.totalInspections}</p>
              </div>
              <ClipboardCheck className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pass Rate</p>
                <p className="text-3xl font-bold text-green-600">{stats.passRate}%</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600 opacity-80" />
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              <span>{stats.totalPass.toLocaleString()} items passed</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Fail Rate</p>
                <p className="text-3xl font-bold text-destructive">{stats.failRate}%</p>
              </div>
              <XCircle className="h-8 w-8 text-destructive opacity-80" />
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              <span>{stats.totalFail.toLocaleString()} items failed</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">N/A Items</p>
                <p className="text-3xl font-bold text-muted-foreground">{stats.totalNA}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-muted-foreground opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pass/Fail Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Response Distribution</CardTitle>
            <CardDescription>Overall pass/fail/N/A breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[250px] items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pass Rate Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pass Rate Trend</CardTitle>
            <CardDescription>Daily pass rate over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={[0, 100]} 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  formatter={(value: any) => value !== null ? [`${value.toFixed(1)}%`, 'Pass Rate'] : ['N/A', 'Pass Rate']}
                />
                <Line 
                  type="monotone" 
                  dataKey="passRate" 
                  stroke="hsl(var(--chart-2))" 
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Inspections by Site */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Inspections by Facility</CardTitle>
          <CardDescription>Number of inspections and pass rates per facility</CardDescription>
        </CardHeader>
        <CardContent>
          {siteData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={siteData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={120}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => {
                    if (name === 'count') return [value, 'Inspections'];
                    if (name === 'passRate') return [`${value.toFixed(1)}%`, 'Pass Rate'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar dataKey="count" name="Inspections" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-muted-foreground">
              No inspection data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Template Performance</CardTitle>
          <CardDescription>Pass rates and issue counts by inspection template</CardDescription>
        </CardHeader>
        <CardContent>
          {templateData.length > 0 ? (
            <div className="space-y-4">
              {templateData.map((template, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center gap-3">
                    <ClipboardCheck className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{template.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {template.count} inspection{template.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">Pass Rate</p>
                      <Badge 
                        variant={template.passRate >= 90 ? 'default' : template.passRate >= 70 ? 'secondary' : 'destructive'}
                      >
                        {template.passRate.toFixed(1)}%
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">Failed Items</p>
                      <Badge variant={template.failItems > 0 ? 'destructive' : 'outline'}>
                        {template.failItems}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-[200px] items-center justify-center text-muted-foreground">
              No template data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
